import { deleteCookie, setCookie } from "@/lib/cookies";
import {
  type ApiResponse,
  type ApiSuccess,
  CF_ORGANIZATION_KEY_NAME,
  TOKEN_KEY_NAME,
  DEVICE_ID_NAME,
  apiClient,
  getDeviceId,
  isApiError,
  TOKEN_EXPIRY_LOCAL_STORAGE_KEY,
} from "./_client";
import { User } from "./user";
import { type AxiosRequestConfig } from "axios";

export interface Credentials {
  email: string;
  password: string;
  mfa?: string;
  isTrusted?: boolean;
}

interface ApiCredentials {
  mfa?: string;
  is_trusted?: boolean;
  device_id?: string;
}

type MFACheck = {
  mfa: boolean;
  mfa_status:
    | "code_valid"
    | "code_invalid"
    | "code_expired"
    | "too_many_attempts"
    | "no_code"
    | "code_generated_too_recently"
    | "code_sent";
  err?: string; // todo find values of this
};

type LoginSuccess = {
  expiry: string;
  token: string;
  user: User;
  device_id?: string;
};

export type AuthResponse = ApiResponse<MFACheck | LoginSuccess>;

export const isMFACheck = (
  maybeMFA: AuthResponse,
): maybeMFA is ApiSuccess<MFACheck> =>
  !isApiError(maybeMFA) && "mfa" in maybeMFA.data;

const isLoginSuccess = (
  maybeLoginSuccess: AuthResponse,
): maybeLoginSuccess is ApiSuccess<LoginSuccess> =>
  !isApiError(maybeLoginSuccess) && !isMFACheck(maybeLoginSuccess);

export const logIn = async (credentials: Credentials) => {
  const options: AxiosRequestConfig<ApiCredentials> = {
    method: "POST",
    url: "api/auth/login/",
    auth: {
      username: credentials.email,
      password: credentials.password,
    },
  };
  options.data = {};
  if (credentials.mfa) {
    options.data.mfa = credentials.mfa;
    options.data.is_trusted = credentials.isTrusted || false;
  }
  const deviceId = getDeviceId();
  if (deviceId) options.data.device_id = deviceId;
  const res = await apiClient.request<never, AuthResponse>(options);
  if (isLoginSuccess(res)) {
    setCookie(TOKEN_KEY_NAME, res.data.token);
    if (res.data.device_id) setCookie(DEVICE_ID_NAME, res.data.device_id);
  }

  return res;
};

export const logOut = async () => {
  const device_id = getDeviceId();
  await apiClient.post<never, ApiResponse>("api/auth/logout/", { device_id });
  localStorage.removeItem(TOKEN_EXPIRY_LOCAL_STORAGE_KEY);
  deleteCookie(CF_ORGANIZATION_KEY_NAME);
  deleteCookie(TOKEN_KEY_NAME);
};

export const softLogOut = () => {
  localStorage.removeItem(TOKEN_EXPIRY_LOCAL_STORAGE_KEY);
  deleteCookie(CF_ORGANIZATION_KEY_NAME);
  deleteCookie(TOKEN_KEY_NAME);
};
